<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import UIKit from '@pocketprep/ui-kit'
import SelectExamsSidePanel from '@/components/SidePanels/EditStudent/SelectExams.vue'
import { formatDate } from '@/utils'
import { examModule } from '@/store/exam/module'
import { bundleModule } from '@/store/bundle/module'
import { groupModule } from '@/store/group/module'
import { instructorModule } from '@/store/instructor/module'
import { studentModule } from '@/store/student/module'
import { organizationModule } from '@/store/organization/module'
import { toastModule } from '@/store/toast/module'
import type { Study } from '@pocketprep/types'
import type { TStudent } from '@/store/student/types'

const {
    Link: PocketLink,
    Select: PocketSelect,
    Tooltip,
    Icon,
    BundleIcon,
} = UIKit

const props = defineProps<{
    licenses: Study.Class.LicenseJSON[]
    studentName: string
    orgStudentId: string
    orgStudent?: TStudent
    orgStudentUser?: Study.Class.UserJSON
}>()

const emit = defineEmits<{
    updateInstructors: [instructorIdsPerExamGuid: { [key: string]: string[] }]
    loading: [isLoading: boolean]
    close: [value?: boolean]
}>()

const showRemoveInstructorTooltip = ref<null | string>(null)
const showAddInstructorTooltip = ref<null | string>(null)
const instructorOptionsPerExamGuid = ref<{ [key: string]: { value: string; label: string }[] }>({})
const showSelectExamsSidePanel = ref(false)

const setRemoveInstructorTooltip = (id: string | null) => {
    showRemoveInstructorTooltip.value = id
}

const clearRemoveInstructorTooltip = () => {
    showRemoveInstructorTooltip.value = null
}

const setAddInstructorTooltip = (id: string | null) => {
    showAddInstructorTooltip.value = id
}

const clearAddInstructorTooltip = () => {
    showAddInstructorTooltip.value = null
}

const closeSelectExamsSidePanel = () => {
    showSelectExamsSidePanel.value = false
}

const currentOrgExams = computed(() => {
    return examModule.getters.getCurrentOrgExams()
})

const guidOrgExamLib = computed(() => {
    return examModule.getters.getGuidOrgExamLib()
})

const orgExamIdGuidLib = computed(() => {
    return examModule.getters.getOrgExamIdGuidLib()
})

const currentOrgGroups = computed(() => {
    return groupModule.getters.getCurrentOrgGroups()
})

const currentOrgInstructors = computed(() => {
    return instructorModule.getters.getCurrentOrgInstructors()
})

const currentOrgInstructor = computed(() => {
    return instructorModule.getters.getCurrentOrgInstructor()
})

const currentOrgInstructorUsers = computed(() => {
    return instructorModule.getters.getCurrentOrgInstructorUsers()
})

const currentOrgStudentUEM = computed(() => {
    return studentModule.getters.getCurrentOrgStudentUEM()
})

const currentStudentCurrentExam = computed(() => {
    return examModule.getters.getCurrentStudentCurrentExam()
})

const currentOrganization = computed(() => {
    return organizationModule.getters.getCurrentOrganization()
})

const mappedInstructorOptionsByExamLib = computed(() => {
    if (!currentOrgGroups.value) {
        return {}
    }

    return Object.values(currentOrgGroups.value)
        .filter((g): g is Required<typeof g> => g.type === 'system' && !!g.owner)
        .reduce((acc, group) => {
            if (!currentOrgInstructors.value || !currentOrgInstructorUsers.value) {
                return acc
            }

            const orgInstructor = currentOrgInstructors.value[group.owner.objectId]
            const user = orgInstructor && currentOrgInstructorUsers.value[orgInstructor.user.objectId]
            const examGuid = orgExamIdGuidLib.value[group.orgExam.objectId]

            if (
                !examGuid
                || !user
                || (user && !user.firstName && !user.lastName)
                || orgInstructor.status === 'archived'
            ) {
                return acc
            }

            if (!(examGuid in acc)) {
                acc[examGuid] = {}
            }

            acc[examGuid] = {
                ...acc[examGuid],
                [orgInstructor.objectId]: {
                    value: orgInstructor.objectId,
                    label: [ user.firstName, user.lastName ].join(' '),
                },
            }

            return acc
        }, {} as { [key: string]: { [key: string]: { value: string; label: string } } })
})

const userExamMetadataGuidLib = computed(() => {
    return props.orgStudentUser?.objectId
        && currentOrgStudentUEM.value[props.orgStudentUser.objectId]?.reduce((acc, uem) => ({
            ...acc,
            [uem.examGuid]: uem,
        }), {} as { [key: string]: Study.Class.UserExamMetadataJSON })
})

const studentExamIds = computed(() => {
    return groupModule.getters.searchCurrentOrgGroups({
        studentId: props.orgStudentId,
        type: 'system',
    }).reduce((acc, group) => !group.owner ? [ ...acc, group.orgExam.objectId ] : acc, [] as string[])
})

const getCurrentInstructorOptionsPerExamGuid = () => {
    if (!currentOrgGroups.value) {
        return {}
    }

    const orgExamInstructorIdLib = Object.values(currentOrgGroups.value)
        .filter((g): g is Required<typeof g> => g.type === 'system' && !!g.owner)
        .reduce((acc, orgGroup) => {
            const orgStudentId = props.licenses[0]?.orgStudent?.objectId

            if (
                !orgStudentId
                || !orgGroup.orgStudents.find(s => s.objectId === orgStudentId)
            ) {
                return acc
            }

            if (!(orgGroup.orgExam.objectId in acc)) {
                acc[orgGroup.orgExam.objectId] = []
            }

            const examGuid = orgExamIdGuidLib.value[orgGroup.orgExam.objectId]
            const instructor = examGuid && mappedInstructorOptionsByExamLib.value[examGuid]
            const option = examGuid && instructor && instructor[orgGroup.owner.objectId]

            if (!option) {
                return acc
            }

            acc[orgGroup.orgExam.objectId]?.push(option)

            return acc
        }, {} as { [key: string]: { value: string; label: string }[] })

    const guidGroupLib = Object.entries(guidOrgExamLib.value).reduce((acc, [ examGuid, orgExams ]) => {
        const currentExam = currentStudentCurrentExam.value || orgExams[0]
        if (!currentExam) {
            return acc
        }
        acc[examGuid] = orgExamInstructorIdLib[currentExam.objectId] || []

        return acc
    }, {} as { [key: string]: { value: string; label: string }[] })

    return guidGroupLib
}

const instructorName = (orgInstructorId: string) => {
    if (
        !currentOrgInstructors.value
        || !currentOrgInstructorUsers.value
        || !currentOrgInstructors.value[orgInstructorId]
    ) {
        return
    }

    const orgInstructor = currentOrgInstructors.value[orgInstructorId]
    const orgInstructorUser = currentOrgInstructorUsers.value[orgInstructor.user.objectId]

    return [ orgInstructorUser?.firstName, orgInstructorUser?.lastName ].join(' ')
}

const emitLoading = (isLoading: boolean) => {
    emit('loading', isLoading)
}

const emitClose = () => {
    emit('close', true)
}

const submitCancelPendingSeat = async (examGuid: string) => {
    const orgStudentId = props.licenses?.[0]?.orgStudent?.objectId

    if (!orgStudentId) {
        throw new Error('Unable to find orgStudent ID')
    }

    if (!currentOrganization.value) {
        throw new Error('Unable to find current organization')
    }

    emitLoading(true)
    await studentModule.actions.cancelPendingSeatsBulk({
        organizationId: currentOrganization.value.objectId,
        orgStudentIds: [ orgStudentId ],
        examGuids: [ examGuid ],
        orgExamIds: guidOrgExamLib.value[examGuid]?.map(orgExam => orgExam.objectId) || [],
    })
    emitLoading(false)
    emitClose()
}

const submitUnarchiveSeat = async (examGuid: string) => {
    const license = props.licenses?.find(l => l.examGuid === examGuid)

    if (!license) {
        throw new Error('submitArchiveSeat: No license found')
    }

    if (!currentOrganization.value) {
        throw new Error('Unable to find current organization')
    }

    await studentModule.actions.unarchiveSeats({
        licenseIds: [ license.objectId ],
        organizationId: currentOrganization.value.objectId,
    })
}

const submitResendLicenseEmail = async (examGuid: string) => {
    const license = props.licenses.find(l => l.examGuid === examGuid)
    if (!license) {
        throw new Error('submitResendLicenseEmail: Unable to find license.')
    }

    await studentModule.actions.resendSeatEmail({ licenseId: license.objectId })

    toastModule.actions.displayToast({
        title: 'Invite sent.',
    })
}

const submitArchiveSeat = async (examGuid: string) => {
    const license = props.licenses?.find(l => l.examGuid === examGuid)
    if (!license) {
        throw new Error('submitArchiveSeat: No license found')
    }
    if (!currentOrgInstructor.value) {
        throw new Error('submitArchiveSeat: No current instructor found')
    }
    instructorOptionsPerExamGuid.value[examGuid] = []

    await studentModule.actions.archiveSeats({
        licenseIds: [ license.objectId ],
        orgInstructorId: currentOrgInstructor.value.objectId,
    })
}

const getScheduledExamDate = (examGuid: string) => {
    const userExamMetadata = userExamMetadataGuidLib.value && userExamMetadataGuidLib.value[examGuid]

    return userExamMetadata && userExamMetadata.scheduledExamDate
        ? formatDate(userExamMetadata.scheduledExamDate.iso)
        : 'Not Scheduled'
}

const latestExamMetadataForGuid = (examGuid: string) => {
    return examModule.getters.getSortedExamMetadataForGuid(examGuid)[0]
}

const examName = (examGuid: string) => {
    const currentExam = currentStudentCurrentExam.value || guidOrgExamLib.value[examGuid]?.[0]
    return currentExam?.displayName || latestExamMetadataForGuid(examGuid)?.nativeAppName
}

const getBundleFromExamGuid = (examGuid: string) => {
    const selectedExamMetadata = latestExamMetadataForGuid(examGuid)
    const bundle = selectedExamMetadata && bundleModule.getters.getExamBundle(selectedExamMetadata.objectId)
    return bundle
}

const getBundleIdFromExamGuid = (examGuid: string) => {
    return getBundleFromExamGuid(examGuid)?.objectId
}

const addInstructor = (examGuid: string) => {
    showAddInstructorTooltip.value = null
    if (!instructorOptionsPerExamGuid.value[examGuid]) {
        instructorOptionsPerExamGuid.value[examGuid] = []
    }

    instructorOptionsPerExamGuid.value[examGuid].push({ value: '', label: '' })
}

const removeInstructor = (examGuid: string, instructorId: string) => {
    showRemoveInstructorTooltip.value = null
    instructorOptionsPerExamGuid.value[examGuid]?.splice(instructorOptionsPerExamGuid.value[examGuid]
        .findIndex(o => o.value === instructorId), 1)
}

const clickAddExam = () => {
    if (Object.values(currentOrgExams.value).length > props.licenses.length) {
        showSelectExamsSidePanel.value = true
    }
}

onMounted(async () => {
    await Promise.all([
        examModule.actions.fetchCurrentOrgExams(),
        groupModule.actions.fetchCurrentOrgGroups(),
        instructorModule.actions.fetchCurrentOrgInstructors(),
        instructorModule.actions.fetchCurrentOrgInstructorUsers(),
    ])

    if (props.orgStudent?.user) {
        await studentModule.actions.fetchOrgStudentUEMForStudents([ props.orgStudent.objectId ])
    }

    instructorOptionsPerExamGuid.value = getCurrentInstructorOptionsPerExamGuid()
})

const getInstructorOptions = (license: { examGuid?: string }): { value: string; label: string }[] => {
    return license.examGuid ? (instructorOptionsPerExamGuid.value[license.examGuid] || []) : []
}

watch(instructorOptionsPerExamGuid, (updatedInstructors) => {
    const instructorIdsPerExamGuid = Object.entries(updatedInstructors).reduce((acc, [ examGuid, options ]) => {
        const currentExam = currentStudentCurrentExam.value || guidOrgExamLib.value[examGuid]?.[0]
        const orgExamId = currentExam?.objectId
        if (!orgExamId) {
            return acc
        }
        acc[orgExamId] = options?.map(o => o.value).filter(o => o) ?? []
        return acc
    }, {} as { [key: string]: string[] })
    emit('updateInstructors', instructorIdsPerExamGuid)
}, { deep: true })
</script>

<template>
    <div>
        <SelectExamsSidePanel
            v-if="showSelectExamsSidePanel"
            :disabled-exam-ids="studentExamIds"
            :organization-id="currentOrganization && currentOrganization.objectId || ''"
            :org-student-id="props.orgStudentId"
            :student-first-name="
                props.orgStudent && props.orgStudent.displayFirstName
                    || props.orgStudentUser && props.orgStudentUser.firstName || ''
            "
            :organization-name="currentOrganization && currentOrganization.name || ''"
            @loading="emitLoading"
            @close="closeSelectExamsSidePanel"
        />
        <div
            v-for="license in props.licenses"
            :key="license.objectId"
            class="licenses-list"
        >
            <BundleIcon
                v-if="license.examGuid"
                class="licenses-list__bundle-icon"
                :bundle-id="getBundleIdFromExamGuid(license.examGuid)"
                :grayscale="license.status !== 'consumed'"
                :style="{ opacity: license.status !== 'consumed' ? '0.5' : '1' }"
            />
            <div
                v-if="license.status !== 'consumed'"
                class="licenses-list__license-pill"
                :class="{ 'licenses-list__license-pill--archived': license.status === 'archived' }"
            >
                {{ license.status === 'pending' ? 'Pending Seat' : 'Archived Seat' }}
            </div>
            <div class="licenses-list__license-exam-title">
                {{ license.examGuid && examName(license.examGuid) }} Exam Prep
            </div>
            <div v-if="license.status === 'pending'" class="licenses-list__license-status">
                {{ props.studentName }} has not accepted invite yet
            </div>
            <div
                v-if="license.examGuid && getInstructorOptions(license).length"
                class="licenses-list__instructors"
            >
                <div class="licenses-list__instructors-label">
                    Instructors
                </div>
                <div
                    v-for="(option, index) in getInstructorOptions(license)"
                    :key="`${license.examGuid}_${option.value}`"
                    class="licenses-list__instructor"
                >
                    <PocketSelect
                        v-if="license.examGuid && mappedInstructorOptionsByExamLib[license.examGuid]"
                        v-model="getInstructorOptions(license)[index]"
                        class="licenses-list__instructor-select"
                        :data="Object.values(mappedInstructorOptionsByExamLib[license.examGuid]!)"
                    />
                    <div
                        class="licenses-list__instructor-remove"
                        @click="license.examGuid && removeInstructor(license.examGuid, option.value)"
                        @mouseover="setRemoveInstructorTooltip(`${index}_${license.objectId}`)"
                        @mouseout="clearRemoveInstructorTooltip"
                    >
                        <Tooltip v-if="showRemoveInstructorTooltip === `${index}_${license.objectId}`">
                            Remove Instructor
                        </Tooltip>
                        <Icon type="close" />
                    </div>
                    <div
                        class="licenses-list__instructor-add"
                        @click="license.examGuid && addInstructor(license.examGuid)"
                        @mouseover="setAddInstructorTooltip(`${index}_${license.objectId}`)"
                        @mouseout="clearAddInstructorTooltip"
                    >
                        <Tooltip v-if="showAddInstructorTooltip === `${index}_${license.objectId}`">
                            Add Instructor
                        </Tooltip>
                        <Icon
                            v-if="
                                license.examGuid
                                    && option.value
                                    && index + 1 === getInstructorOptions(license).length
                            "
                            type="addCircle"
                        />
                    </div>
                </div>
            </div>
            <div class="licenses-list__license-details">
                <div v-if="license.redeemedDate && license.status === 'consumed'" class="licenses-list__license-detail">
                    <div>
                        Started prep
                    </div>
                    <div>
                        {{ formatDate(license.redeemedDate.iso) }}
                    </div>
                </div>
                <div v-if="license.invitedBy && license.status !== 'archived'" class="licenses-list__license-detail">
                    <div>
                        Added by
                    </div>
                    <div>
                        {{ instructorName(license.invitedBy.objectId) }}
                    </div>
                </div>
                <div v-if="license.archivedBy && license.status === 'archived'" class="licenses-list__license-detail">
                    <div>
                        Archived by
                    </div>
                    <div>
                        {{ instructorName(license.archivedBy.objectId) }}
                    </div>
                </div>
                <div v-if="license.invitedAt && license.status === 'pending'" class="licenses-list__license-detail">
                    <div>
                        Invite sent
                    </div>
                    <div>
                        {{ formatDate(license.invitedAt.iso) }}
                    </div>
                </div>
                <div
                    v-if="
                        license.examGuid
                            && userExamMetadataGuidLib
                            && userExamMetadataGuidLib[license.examGuid]
                    "
                    class="licenses-list__license-detail"
                >
                    <div>
                        Exam date
                    </div>
                    <div>
                        {{ getScheduledExamDate(license.examGuid) }}
                    </div>
                </div>
                <div v-if="license.status === 'archived'" class="licenses-list__license-detail">
                    <div>
                        Archived on
                    </div>
                    <div>
                        {{ formatDate(license.updatedAt) }}
                    </div>
                </div>
            </div>
            <PocketLink
                v-if="license.status === 'pending'"
                type="tertiary"
                @click.prevent="license.examGuid && submitCancelPendingSeat(license.examGuid)"
            >
                Cancel Invite
            </PocketLink>
            <PocketLink
                v-if="license.status === 'pending'"
                type="tertiary"
                @click="license.examGuid && submitResendLicenseEmail(license.examGuid)"
            >
                Resend Email
            </PocketLink>
            <PocketLink
                v-if="
                    license.status !== 'archived'
                        && license.examGuid
                        && !getInstructorOptions(license).length"
                type="tertiary"
                @click.prevent="license.examGuid && addInstructor(license.examGuid)"
            >
                Add Instructor
            </PocketLink>
            <PocketLink
                v-if="license.status === 'consumed'"
                type="tertiary-red"
                @click.prevent="license.examGuid && submitArchiveSeat(license.examGuid)"
            >
                Archive Seat
            </PocketLink>
            <PocketLink
                v-if="license.status === 'archived'"
                type="tertiary"
                @click.prevent="license.examGuid && submitUnarchiveSeat(license.examGuid)"
            >
                Unarchive Seat
            </PocketLink>
        </div>
        <PocketLink
            type="tertiary"
            class="licenses-list__add-exam"
            :disabled="Object.values(currentOrgExams).length <= props.licenses.length"
            @click.prevent="clickAddExam"
        >
            Add another exam <Icon type="arrow" class="licenses-list__add-exam-arrow" />
        </PocketLink>
    </div>
</template>

<style lang="scss" scoped>
.licenses-list {
    background: $white;
    box-shadow: 0 4px 16px 0 rgba($brand-black, 0.07), 0 1px 4px 0 rgba($brand-black, 0.13);
    border-radius: 6px;
    padding: 24px 26px;
    width: 380px;
    margin-bottom: 20px;
    position: relative;
    z-index: 1;

    &__bundle-icon {
        position: absolute;
        right: 13px;
        top: 11px;
        width: 50px;
    }

    &__license-pill {
        background: $beach-sand;
        height: 20px;
        width: 92px;
        line-height: 20px;
        font-weight: 600;
        color: $flat-brown;
        margin-bottom: 20px;
        font-size: 13px;
        border-radius: 3px;
        text-align: center;

        &--archived {
            color: $white;
            background: $pewter;
            width: 95px;
        }
    }

    &__license-exam-title {
        font-size: 17px;
        font-weight: 600;
        line-height: 23px;
        margin-bottom: 3px;
    }

    &__license-status {
        font-size: 13px;
        font-weight: 500;
        margin-bottom: -7px;
    }

    &__instructors {
        margin-bottom: 14px;
        margin-top: 23px;

        .licenses-list__instructors-label {
            font-size: 13px;
            line-height: 14px;
            margin: 0 0 6px;
            color: $slate-01;
        }

        .licenses-list__instructor {
            margin-left: -10px;
            display: flex;
            align-items: center;
            margin-bottom: 6px;

            .licenses-list__instructor-select {
                width: 275px;
            }

            .licenses-list__instructor-add,
            .licenses-list__instructor-remove {
                position: relative;
                margin-left: 11px;
                height: 20px;

                svg {
                    cursor: pointer;
                    color: $brand-blue;
                }

                &:hover svg {
                    color: $brand-black;
                }

                .uikit-tooltip {
                    position: absolute;
                    top: -34px;
                    left: 10px;
                }
            }
        }
    }

    &__license-details {
        font-size: 13px;
        line-height: 21px;
        margin-bottom: 22px;
        margin-top: 20px;
        width: 185px;

        .licenses-list__license-detail {
            display: flex;
            justify-content: space-between;

            :first-child {
                color: $slate-01;
                min-width: 75px;
            }

            :last-child {
                text-align: right;
            }
        }
    }

    a {
        display: block;
        margin-bottom: 9px;
        font-size: 14px !important;
        line-height: 19px !important;
        font-weight: 600;

        &:last-child {
            margin-bottom: 0;
        }
    }

    &__add-exam {
        padding: 0 12px;
        display: block;
        font-size: 14px !important;
    }

    &__add-exam-arrow {
        margin-left: 6px;
    }
}
</style>
